import React, { useContext } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination, Autoplay, A11y } from "swiper";

import { TeaserLayoutWrapper } from "../../../components/teaser-components/TeaserLayoutWrapper/TeaserLayoutWrapper";
import { CameraTeaserElement } from "./CameraTeaserElement/CameraTeaserElement";
import { CameraAccessoryTeaserElement } from "./CameraAccessoryTeaserElement/CameraAccessoryTeaserElement";

import * as styles from "./CameraTeaser.module.less";
import { useDynamicPager } from "../../../lib/hooks/useDynamicPager";
import { Button } from "../../../components/Button";
import { generateClassString as gcs } from "../../../lib/helper/generateClassString";
import {
    useIsDesktop,
    useIsSmaller1400,
    useIsTablet
} from "../../../lib/hooks/mediaQueries";
import GeneralPageContext from "../../../templates/GeneralPageContext";
import { slugify } from "../../../lib/helper/getAnchorNavData";

const ITEMS_PER_PAGE = 9;

export function CameraTeaser({
    data: {
        anker,
        tagline,
        headline,
        semanticOfHeadline,
        semanticOfTeaserHeadline,
        background,
        display,
        ...data
    },
    disablePager = false,
    type,
    hasLightBackground,
    ...props
}) {
    const {
        layout: { showAnchorNavigation }
    } = useContext(GeneralPageContext);

    const sectionId = anker || "camera-teaser-section";

    const isSmaller1400 = useIsSmaller1400();
    const isMobile = useIsTablet();
    const isDesktop = useIsDesktop();
    const maxTeaserWidth = 430;
    const windowWidth =
        typeof window !== "undefined"
            ? window.innerWidth > 1440
                ? 1440
                : window.innerWidth
            : maxTeaserWidth;

    const slidesPerView = Math.max(windowWidth / maxTeaserWidth, 1.2);

    const idButtonPrev = "idButtonPrev-12345";
    const idButtonNext = "idButtonNext-123456";

    // improvement: could be done with HOC withTeaserConfig
    const teaserMap = {
        DatoCmsCameraTeaser: {
            element: CameraTeaserElement,
            teaserList: data.selectedCameras
        },
        DatoCmsCameraAccessorieTeaser: {
            element: CameraAccessoryTeaserElement,
            teaserList: data.selectedCameraAccessories
        }
    };

    const { element: TeaserElement, teaserList } = teaserMap[type];

    const { pagedItems: pagesTeaserElements, Component: DynamicPager } =
        useDynamicPager({
            items: teaserList,
            itemsPerPage: ITEMS_PER_PAGE,
            elementToScroll: slugify(sectionId)
        });

    if (!teaserList && !teaserList.length) {
        console.warn("teaser with type: " + type + " is undefined or empty.");
        return null;
    }

    return (
        <TeaserLayoutWrapper
            anker={sectionId}
            tagline={tagline}
            headline={headline}
            semanticOfHeadline={semanticOfHeadline}
            background={background}
            hasLightBackground={hasLightBackground}
            wrapperClass={display === "slider" ? " " : styles.wrapper}
        >
            {display === "slider" && (
                <>
                    <div className={styles.mediaSliderControls}>
                        <Button
                            id={idButtonPrev}
                            type="prev"
                            icon="arrowLeft"
                        />
                        <Button
                            id={idButtonNext}
                            type="next"
                            icon="arrowRight"
                        />
                    </div>
                    <Swiper
                        className={gcs(
                            styles.teaserContainer,
                            isMobile ? "mobileTeasers" : " ",
                            [
                                type === "DatoCmsCameraAccessorieTeaser",
                                "accessoryTeaser"
                            ]
                        )}
                        modules={[Navigation, Pagination, Autoplay, A11y]}
                        slidesPerView={
                            showAnchorNavigation && !isDesktop
                                ? 2
                                : isMobile
                                ? 1.125
                                : isSmaller1400
                                ? 2.3
                                : slidesPerView
                        }
                        allowSlidePrev={true}
                        navigation={{
                            prevEl: `#${idButtonPrev}`,
                            nextEl: `#${idButtonNext}`
                        }}
                    >
                        {teaserList.map((teaserElement) => {
                            return (
                                <SwiperSlide
                                    key={teaserElement.id}
                                    className={styles.teaser}
                                >
                                    <TeaserElement
                                        data={teaserElement}
                                        semanticOfTeaserHeadline={
                                            semanticOfTeaserHeadline
                                        }
                                        hasLightBackground={hasLightBackground}
                                        currency={props.config.currency}
                                    />
                                </SwiperSlide>
                            );
                        })}
                    </Swiper>
                </>
            )}

            {display === "grid" && (
                <>
                    <div className={styles.cameraTeaserGrid}>
                        {pagesTeaserElements.map((teaserElement) => (
                            <TeaserElement
                                key={teaserElement.id}
                                data={teaserElement}
                                semanticOfTeaserHeadline={
                                    semanticOfTeaserHeadline
                                }
                                currency={props.config.currency}
                                hasLightBackground={hasLightBackground}
                            />
                        ))}
                    </div>
                    <div className={styles.pager}>
                        {!disablePager &&
                            teaserList.length > ITEMS_PER_PAGE && (
                                <DynamicPager />
                            )}
                    </div>
                </>
            )}
        </TeaserLayoutWrapper>
    );
}
